import { Observable, Subject } from 'rxjs';
import { ParkbaseStorageService } from 'src/app/@shared/services/parkbaseStorage.service';
import { StatusPageService } from '../../services/status-page.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NotificationGroup } from 'src/app/@core/domain/interfaces/notifications.interface';
import { Definition, FilterSet } from 'src/app/@core/domain/interfaces/definition.interface';
import { NotificationLevel } from 'src/app/@core/domain/enums/notification-type.enum';
import { NotificationsApiService } from 'src/app/@core/api/notification-api.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { Facility } from '../../../../@core/domain/interfaces/facility.interface';
import { debounceTime } from 'rxjs/operators';
import { LogService } from 'src/app/@core';
import { environment } from 'src/environments/environment';
import * as dayjs from 'dayjs';
import { TrackingService } from 'src/app/@shared/services/tracking.service';
import { NgbAccordion } from '@ng-bootstrap/ng-bootstrap';

export interface NotificationsCard {
  notificationGroups: NotificationGroup[];
  placeholders: string[];
  filterSet: FilterSet;
  levels: number[];
  types: number[];
  terminalIds: number[];
  definitions: Definition[];
  loading: boolean;
  pageToLoadNext: number;
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() facilitySelection!: string;
  @Input() facilityId!: number;

  cardData$!: Observable<NotificationsCard>;

  firstNotificationCard: NotificationsCard = {
    notificationGroups: [],
    placeholders: [],
    filterSet: {
      from: dayjs().subtract(1, 'day'),
      filtersSelected: [],
    },
    levels: [],
    types: [],
    terminalIds: [],
    definitions: [],
    loading: false,
    pageToLoadNext: 1,
  };
  pageSize = 25;

  levels: number[] = [];
  types: number[] = [];
  terminalIds: number[] = [];
  definitions!: Definition[];

  filterAlarms!: boolean;
  filterErrors!: boolean;
  filterWarnings!: boolean;
  filterInfo!: boolean;
  notificationLevel!: any;
  notifications!: any;
  placeholders!: any;
  interval;
  refreshNotifications = true;
  opened = false;
  locale!: string;
  selectedOption = '0';
  onFacilitiesPage = false;

  @ViewChildren(NgbAccordion) pageAccordions!: QueryList<NgbAccordion>;

  private buttonClicked = new Subject<string>();

  constructor(
    private notificationsApiService: NotificationsApiService,
    private parkbaseStorageService: ParkbaseStorageService,
    private statusPageService: StatusPageService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private routes: ActivatedRoute,
    private logger: LogService,
    private trackingService: TrackingService
  ) {
    this.interval = setInterval(() => {
      if (this.refreshNotifications) {
        this.refreshNotificationsFromApi();
      }
    }, environment.refreshInterval);

    if (this.statusPageService && this.statusPageService.refreshNotifications) {
      this.statusPageService.refreshNotifications.subscribe((status) => {
        this.refreshNotifications = status;
        if (status) {
          this.firstNotificationCard.notificationGroups = [];
          this.firstNotificationCard.pageToLoadNext = 1;

          this.refreshFilterSelection();
          this.refreshNotificationsFromApi();
        }
      });
    }

    this.routes.params.subscribe((params: Params) => {
      if (params && params['id']) {
        console.log('params', params);
      }
    });

    const buttonClickedDebounced = this.buttonClicked.pipe(debounceTime(200));

    buttonClickedDebounced.subscribe((buttonClicked) => {
      this.filterNotifications(buttonClicked);
    });
  }

  counter(i: number): any[] {
    return new Array(i);
  }

  format(date: string, typeAlarm: boolean): string {
    const dateformatChoice = this.parkbaseStorageService.retrieveDateformatChoice();
    if (dateformatChoice === 'Exact' && typeAlarm) {
      return this.statusPageService.formatExactTime(date);
    } else {
      return this.statusPageService.formatRelativeTime(date);
    }
  }

  open(index: number): void {
    this.opened = !this.opened;
    this.closeAll(index);
  }

  closeAll(index: number) {
    const activeIds = 'static-' + index;
    this.pageAccordions.forEach((acc) => {
      if (acc.activeIds[0] !== activeIds) {
        acc.collapseAll();
      } else {
        acc.expand(activeIds);
      }
    });
  }

  getIconType(notificationGroup: NotificationGroup): string {
    if (notificationGroup && notificationGroup.terminal) {
      const terminalTypeEnumValue = Object.values(notificationGroup.terminal.type)[0];
      return 'terminalType_' + terminalTypeEnumValue;
    }
    return '';
  }

  ngAfterViewInit(): void {
    this.pageAccordions.changes.subscribe((ac) => {
      ac.toArray().forEach((el: any) => {
        el.toggle();
        this.cdr.detectChanges();
      });
    });
  }

  ngOnInit(): void {
    if (this.parkbaseStorageService.getStorage().has('PBNotificationTypeFilterSet')) {
      this.firstNotificationCard.filterSet = this.parkbaseStorageService.retrieveFilterSet();
    }

    if (this.parkbaseStorageService.getStorage().has('PBFacilitySelected')) {
      const selection: Facility = this.parkbaseStorageService.retrieveFacilitySelection();
      if (selection && selection.id === 0) {
        this.onFacilitiesPage = true;
      }
    }

    this.statusPageService.filterNotifications.subscribe((filter) => {
      if (filter) {
        this.terminalIds = filter.terminalIds;
        this.refreshFilterSelection();
        this.refreshNotificationsFromApi();
      }
    });

    this.locale = this.statusPageService.getLocale();

    this.cardData$ = this.notificationsApiService.cardData;
  }

  navigateToFacility(notificationGroup: NotificationGroup): void {
    this.router.navigate(['/status/facilities/' + notificationGroup.facility.id], {
      skipLocationChange: true,
    });
  }

  loadPrevious() {}

  loadNext(cardData: any) {
    if (cardData.loading) {
      return;
    }

    cardData.loading = true;
    cardData.placeholders = new Array(this.pageSize);
    cardData.levels = this.levels;
    cardData.types = this.types;
    cardData.terminalIds = this.terminalIds;
    cardData.definitions = this.definitions;

    const selection: Facility = this.parkbaseStorageService.retrieveFacilitySelection();
    if (selection && selection.id && selection.id > 0 && this.facilityId) {
      this.facilityId = selection.id;
      this.facilitySelection = 'notificationsForFacility';
    }

    if (this.facilitySelection === 'divisionsNotifications') {
      this.notificationsApiService.loadAsyncWithCardData(
        cardData,
        this.pageSize,
        'divisionsNotifications'
      );
    }
    if (this.facilitySelection === 'notificationsForFacility') {
      this.notificationsApiService.loadAsyncWithCardData(
        cardData,
        this.pageSize,
        'notificationsForFacility',
        this.facilityId
      );
    }
  }

  refreshNotificationsFromApi(): void {
    this.firstNotificationCard.filterSet = this.parkbaseStorageService.retrieveFilterSet();
    this.firstNotificationCard.types = this.types;
    this.firstNotificationCard.levels = this.levels;
    this.firstNotificationCard.terminalIds = this.terminalIds;

    if (this.facilitySelection === 'divisionsNotifications') {
      this.notificationsApiService.loadAsyncWithCardData(
        this.firstNotificationCard,
        this.pageSize,
        'divisionsNotifications'
      );
    }
    if (this.facilitySelection === 'notificationsForFacility') {
      this.notificationsApiService.loadAsyncWithCardData(
        this.firstNotificationCard,
        this.pageSize,
        'notificationsForFacility',
        this.facilityId
      );
    }
  }

  refreshFilterSelection(): void {
    this.handlePBDefinitionLists();
    this.handlePBNotificationLevelFilterSet();
  }

  handlePBDefinitionLists(): void {
    this.types = [];
    if (this.parkbaseStorageService.getStorage().has('PBNotificationTypeFilterSet')) {
      this.firstNotificationCard.filterSet = this.parkbaseStorageService.retrieveFilterSet();
      this.firstNotificationCard.filterSet.filtersSelected.forEach((filter) => {
        if (filter.checked) {
          this.types.push(filter.id);
        }
      });
    }
  }

  handlePBNotificationLevelFilterSet(): void {
    if (this.parkbaseStorageService.getStorage().has('PBNotificationLevelFilterSet')) {
      this.levels = this.parkbaseStorageService.retrieveNotificationLevelFilterSet();
      this.levels.forEach((level) => {
        switch (level) {
          case 6:
            this.filterAlarms = true;
            break;
          case 2:
            this.filterErrors = true;
            break;
          case 1:
            this.filterWarnings = true;
            break;
          case 0:
            this.filterInfo = true;
            break;
        }
      });
      this.firstNotificationCard.levels = this.levels;
    }
  }

  onFiltersSelected(filtersSet: any): void {
    this.types = [];
    filtersSet.filtersSelected.forEach((filter: any) => {
      if (filter.checked) {
        this.types.push(filter.id);
      }
    });
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.filterSet = filtersSet;
    this.firstNotificationCard.pageToLoadNext = 1;
    this.firstNotificationCard.types = this.types;
    this.refreshNotificationsFromApi();
  }

  clickFilterButton(button: string) {
    this.buttonClicked.next(button);
  }

  filterNotifications(action: string) {
    switch (action) {
      case 'alarms':
        this.setFilterAlarms();
        this.trackingService.eventEmitter(
          'notificationFilter',
          'action',
          'setFilterAlarms',
          'alarms',
          0
        );
        break;
      case 'errors':
        this.setFilterErrors();
        this.trackingService.eventEmitter(
          'notificationFilter',
          'action',
          'setFilterErrors',
          'errors',
          0
        );
        break;
      case 'warnings':
        this.setFilterWarnings();
        this.trackingService.eventEmitter(
          'notificationFilter',
          'action',
          'setFilterWarnings',
          'warnings',
          0
        );
        break;
      case 'info':
        this.setFilterInfo();
        this.trackingService.eventEmitter(
          'notificationFilter',
          'action',
          'setFilterInfo',
          'info',
          0
        );
        break;
    }
  }

  setFilterAlarms(): void {
    this.filterAlarms = !this.filterAlarms;
    this.notificationLevel = NotificationLevel.Alarm;
    if (this.filterAlarms) {
      this.levels.push(NotificationLevel.Alarm);
    } else {
      this.filterLevels(NotificationLevel.Alarm);
    }
    this.parkbaseStorageService.storeNotificationLevelFilterSet(this.levels);
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.pageToLoadNext = 1;
    this.firstNotificationCard.levels = this.levels;
    this.refreshNotificationsFromApi();
  }

  setFilterErrors(): void {
    this.filterErrors = !this.filterErrors;
    this.notificationLevel = NotificationLevel.Error;
    if (this.filterErrors) {
      this.levels.push(NotificationLevel.Error);
    } else {
      this.filterLevels(NotificationLevel.Error);
    }
    this.parkbaseStorageService.storeNotificationLevelFilterSet(this.levels);
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.pageToLoadNext = 1;
    this.firstNotificationCard.levels = this.levels;
    this.refreshNotificationsFromApi();
  }

  setFilterWarnings(): void {
    this.filterWarnings = !this.filterWarnings;
    this.notificationLevel = NotificationLevel.Warning;
    if (this.filterWarnings) {
      this.levels.push(NotificationLevel.Warning);
    } else {
      this.filterLevels(NotificationLevel.Warning);
    }
    this.parkbaseStorageService.storeNotificationLevelFilterSet(this.levels);
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.pageToLoadNext = 1;
    this.firstNotificationCard.levels = this.levels;
    this.refreshNotificationsFromApi();
  }

  setFilterInfo(): void {
    this.filterInfo = !this.filterInfo;
    this.notificationLevel = NotificationLevel.Info;
    if (this.filterInfo) {
      this.levels.push(NotificationLevel.Info);
    } else {
      this.filterLevels(NotificationLevel.Info);
    }
    this.parkbaseStorageService.storeNotificationLevelFilterSet(this.levels);
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.pageToLoadNext = 1;
    this.firstNotificationCard.levels = this.levels;
    this.refreshNotificationsFromApi();
  }

  filterLevels(level: number): void {
    this.levels = this.levels.filter((value, index, arr) => {
      return value !== level;
    });
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  onSelectedChange($event: any) {
    const currentDate = dayjs();
    if ($event === '0') {
      this.firstNotificationCard.filterSet.from = currentDate.subtract(1, 'day');
    }
    if ($event === '1') {
      this.firstNotificationCard.filterSet.from = currentDate.subtract(7, 'day');
    }
    if ($event === '2') {
      this.firstNotificationCard.filterSet.from = currentDate.subtract(31, 'day');
    }
    this.firstNotificationCard.notificationGroups = [];
    this.firstNotificationCard.pageToLoadNext = 1;
    if (this.facilitySelection === 'divisionsNotifications') {
      this.notificationsApiService.loadAsyncWithCardData(
        this.firstNotificationCard,
        this.pageSize,
        'divisionsNotifications'
      );
    }
    if (this.facilitySelection === 'notificationsForFacility') {
      this.notificationsApiService.loadAsyncWithCardData(
        this.firstNotificationCard,
        this.pageSize,
        'notificationsForFacility',
        this.facilityId
      );
    }
  }
}
